import Backbone from 'backbone'
import _ from 'lodash'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import DataPreviewPanel from './data-preview-panel'
import template from './data-preview.hbs'

/**
 * This is a base class for displaying row/column based previews.
 * It is designed to be used a step inside of a Wizard
 *
 * The following functions must be implemented:
 * 	- submit(event) handles the final submission of the wizard workflow.
 *
 */
const DataPreview = Backbone.View.extend({
	tagName: 'form',

	/**
	 *
	 * @param {String} [options.preview.title=Preview] Dialog header text
	 * @param {String} [options.preview.label=''] If preview is not based on an import file, pass a label
	 * @param {Object} options.preview.data The preview data, JSON serialization of ImportPreview.java
	 * @param {Wizard} options.wizard
	 */
	initialize(options = {}) {
		this.options = options

		var TheFormView = FormView.extend({
			submit: this.submit.bind(this),
			test: this.test.bind(this)
		})

		this.formView = new TheFormView({
			el: this.el,
			allowCleanSubmission: true
		})

		this.title = _.get(this.options, 'preview.title', i18n.get('common.preview'))
		this.label = _.get(this.options, 'preview.label', '')
		this.data = _.get(this.options, 'preview.data', {})

		this.dataPreviewPanel = new DataPreviewPanel({
			label: this.label,
			preview: this.data
		})
	},

	render() {
		const {title, label} = this
		this.$el.html(template({ title, label}))

		this.options.wizard.render({
			formView: this.formView
		})

		this.formView.start()

		let errorMessages = []

		if (!_.isEmpty(this.cellErrors())) {
			// If there are any cell-level errors, add the generic "red cells below are invalid" message
			// #21993 if all of the rows have errors, though, add the "all rows have errors" message
			let message = this.hasValidRows() ? 'admin.import.users.hasErrors' : 'admin.import.noValidRows'
			errorMessages.push(i18n.get(message))
		}

		let topLevelErrors = this.generalErrors()
		if (!_.isEmpty(topLevelErrors)) {
			// Add each "top level" error
			topLevelErrors.forEach(e => errorMessages.push(topLevelErrors))
		}

		if (errorMessages.length > 0) {
			this.formView.errorMessages(errorMessages, { prelude: i18n.get('admin.import.hasErrors') })
		}

		this.$('.dialog-body-content').append(this.dataPreviewPanel.el)
		this.dataPreviewPanel.render()
		return this
	},

	test() {
		// We only block import for "top level" errors (like a missing required column).
		// Cell-level errors don't block the import; those rows are just skipped
		// #21993 However, if **all** of the rows have cell-level errors, nothing will
		// be imported, so block it
		return this.generalErrors().length > 0 || !this.hasValidRows()
	},

	/**
	 * Returns any errors attached to specific cells
	 */
	cellErrors() {
		return _.flatMap(this.data.rows, r => {
			return _(r.cells).filter(c => !!c.error).map('error').value()
		})
	},

	/**
	 * Returns any top-level errors (like a missing username column)
	 */
	generalErrors() {
		return _.get(this.data, 'errors') || []
	},

	/**
	 * Returns true if there are any rows without cell-level errors
	 */
	hasValidRows() {
		let rowsWithErrors = _.filter(this.data.rows, r => {
			return _.some(r.cells, c => !!c.error)
		})

		return rowsWithErrors.length !== this.data.rows.length
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.wizardProgress, this.formView, this.dataPreviewPanel], 'remove')
	}
})

export default DataPreview

DataPreview.__filename__ = 'components/data-preview.js'
